import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import LogoIcon from '../Logo';

const LINKS = [
  { to: '/#services', label: 'Services' },
  { to: '/#projects', label: 'Projects' },
  { to: '/about', label: 'About' },
  { to: '/#reviews', label: 'Reviews' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <nav
        className={`fixed inset-x-0 top-0 z-[70] transition-all duration-500 ${
          scrolled ? 'glass py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 md:px-10">
          <Link to="/" className="flex items-center gap-3">
            <LogoIcon className="h-9 w-9" />
            {/* mobile: logo only until the page is scrolled, then the wordmark fades in */}
            <span
              className={`font-display text-base font-semibold uppercase tracking-[0.18em] text-steel-100 transition-opacity duration-500 md:opacity-100 ${
                scrolled ? 'opacity-100' : 'opacity-0'
              }`}
            >
              Revive Builders
            </span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm text-steel-300 transition-colors duration-300 hover:text-gold-300"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="border border-gold-500/40 px-5 py-2 text-xs uppercase tracking-[0.2em] text-gold-300 transition-all duration-300 hover:bg-gold-500 hover:text-ink-950"
            >
              Request a Bid
            </Link>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="text-steel-100 md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>

      {/* mobile overlay */}
      <div
        className={`fixed inset-0 z-[85] flex flex-col bg-ink-950/95 backdrop-blur-xl transition-all duration-500 md:hidden ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <LogoIcon className="h-9 w-9" />
            <span className="font-display text-base font-semibold uppercase tracking-[0.18em] text-steel-100">
              Revive Builders
            </span>
          </div>
          <button onClick={() => setOpen(false)} className="text-steel-100" aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-8">
          {LINKS.map((l, i) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              style={{ transitionDelay: open ? `${i * 60 + 100}ms` : '0ms' }}
              className={`font-display text-3xl text-steel-100 transition-all duration-500 hover:text-gold-300 ${
                open ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            style={{ transitionDelay: open ? '400ms' : '0ms' }}
            className={`mt-4 bg-gold-500 px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-ink-950 transition-all duration-500 ${
              open ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            Request a Bid
          </Link>
        </div>
      </div>
    </>
  );
}
